import * as React from "react"
import { ChartData } from "../subsection"
import type { TableRowMeansTable } from "../../layers/categorical_layer_view"
import {
    computeTableRowMeanDifference,
    tableRowMeansForTableNode,
} from "../../layers/categorical_layer_view"
import { TableRowMeanBars, TableRowMeanSort } from "./table_row_mean_bars"
import {
    SPIDER_SERIES_COLORS,
    TableRowMeanSpider,
    type TableRowMeanSpiderSeries,
} from "./table_row_mean_spider"

export type TableMeanCompareOption = {
    layerId: number
    name: string
}

export type TableRowMeanVariant = "bars" | "spider"

export type TableMeanChartSettings = {
    sortType: TableRowMeanSort
    chartView: "primary" | "diff"
    selectedTableNodeId: number
    primaryVisible: boolean
    /** When true, difference is compare − primary instead of primary − compare. */
    diffSwapped: boolean
}

export const DEFAULT_TABLE_MEAN_CHART_SETTINGS: TableMeanChartSettings = {
    sortType: "tableOrder",
    chartView: "primary",
    selectedTableNodeId: -1,
    primaryVisible: true,
    diffSwapped: false,
}

interface TableRowMeanChartProps {
    chartData: ChartData
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
    variant?: TableRowMeanVariant
    compareOptions?: TableMeanCompareOption[]
    /** Bar chart: single compare layer for difference view. */
    compareLayerId?: number | null
    onCompareLayerIdChange?: (layerId: number | null) => void
    compareChartData?: ChartData
    /** Spider chart: overlay multiple comparable layers on one diagram. */
    overlayLayerIds?: number[]
    onOverlayLayerIdsChange?: (layerIds: number[]) => void
    overlayChartDataByLayerId?: Record<number, ChartData>
    primaryLayerName?: string
    settings?: TableMeanChartSettings
    onSettingsChange?: (patch: Partial<TableMeanChartSettings>) => void
}

function tablesFromChartData(chartData: ChartData): TableRowMeansTable[] {
    if (chartData.tableRowMeansTables?.length) {
        return chartData.tableRowMeansTables
    }
    if (chartData.tableRowMeans?.length) {
        return [{
            nodeId: -1,
            name: chartData.tableName ?? "Table",
            rows: chartData.tableRowMeans,
        }]
    }
    return []
}

export const GenerateTableRowMeanChart = ({
    chartData,
    compact,
    panelWidth,
    panelHeight,
    svgId = "tableMean",
    variant = "bars",
    compareOptions = [],
    compareLayerId = null,
    onCompareLayerIdChange,
    compareChartData,
    overlayLayerIds = [],
    onOverlayLayerIdsChange,
    overlayChartDataByLayerId = {},
    primaryLayerName,
    settings: settingsProp,
    onSettingsChange,
}: TableRowMeanChartProps) => {
    const tables = tablesFromChartData(chartData)
    const snapshotTablesKey = React.useMemo(
        () => JSON.stringify(tables.map(table => ({ nodeId: table.nodeId, rows: table.rows }))),
        [tables]
    )
    const [localSettings, setLocalSettings] = React.useState<TableMeanChartSettings>(DEFAULT_TABLE_MEAN_CHART_SETTINGS)
    const settings = settingsProp ?? localSettings
    const patchSettings = React.useCallback((patch: Partial<TableMeanChartSettings>) => {
        if (onSettingsChange) {
            onSettingsChange(patch)
            return
        }
        setLocalSettings(prev => ({ ...prev, ...patch }))
    }, [onSettingsChange])
    const { sortType, chartView, selectedTableNodeId, primaryVisible, diffSwapped = false } = settings

    React.useEffect(() => {
        if (tables.length === 0) {
            if (selectedTableNodeId !== -1) patchSettings({ selectedTableNodeId: -1 })
            return
        }
        if (!tables.some(table => table.nodeId === selectedTableNodeId)) {
            patchSettings({ selectedTableNodeId: tables[0].nodeId })
        }
    }, [tables, selectedTableNodeId, patchSettings])

    const selectedTable = tables.find(table => table.nodeId === selectedTableNodeId) ?? tables[0]
    const primaryRows = selectedTable?.rows ?? []

    const compareLayerName = compareOptions.find(option => option.layerId === compareLayerId)?.name
    const compareRows = compareChartData && compareLayerId !== null
        ? tableRowMeansForTableNode(compareChartData, selectedTableNodeId)
        : []

    const minuendName = (diffSwapped ? compareLayerName : primaryLayerName) ?? (diffSwapped ? "Compare" : "Base")
    const subtrahendName = (diffSwapped ? primaryLayerName : compareLayerName) ?? (diffSwapped ? "Base" : "Compare")
    const minuendRows = diffSwapped ? compareRows : primaryRows
    const subtrahendRows = diffSwapped ? primaryRows : compareRows

    const diffRows = React.useMemo(
        () => (compareRows.length > 0 ? computeTableRowMeanDifference(minuendRows, subtrahendRows) : []),
        [compareRows, minuendRows, subtrahendRows]
    )

    const showCompareSelect = variant === "bars"
        && compareOptions.length > 0
        && onCompareLayerIdChange !== undefined
    const showSpiderLegend = variant === "spider"
    const showDiffChart = variant === "bars"
        && compareLayerId !== null
        && compareChartData !== undefined
        && diffRows.length > 0

    React.useEffect(() => {
        if (!showDiffChart && chartView !== "primary") {
            patchSettings({ chartView: "primary" })
        }
    }, [showDiffChart, compareLayerId, selectedTableNodeId, chartView, patchSettings])

    const primaryAnimationKey = `${snapshotTablesKey}:${selectedTableNodeId}`
    const diffAnimationKey = `${primaryAnimationKey}:compare:${compareLayerId}:swap:${diffSwapped}:${JSON.stringify(diffRows)}`
    const overlayAnimationKey = `${primaryAnimationKey}:primary:${primaryVisible}:overlay:${overlayLayerIds.join(",")}`

    const showingDiff = showDiffChart && chartView === "diff"
    const visibleRows = showingDiff ? diffRows : primaryRows
    const visibleSvgId = showingDiff ? `${svgId}-diff` : svgId
    const visibleAnimationKey = showingDiff ? diffAnimationKey : primaryAnimationKey
    const primaryName = primaryLayerName ?? "Base"
    const chartTitle = showingDiff ? `${minuendName} → ${subtrahendName}` : primaryName
    const titleNameMaxWidthPx = Math.max(
        showingDiff ? 72 : 140,
        Math.round(
            showingDiff
                ? ((panelWidth ?? (compact ? 220 : 420)) - (compact ? 72 : 88)) / 2
                : (panelWidth ?? (compact ? 220 : 420)) - (compact ? 24 : 32)
        )
    )

    const toggleOverlayLayer = (layerId: number) => {
        if (!onOverlayLayerIdsChange) return
        if (overlayLayerIds.includes(layerId)) {
            onOverlayLayerIdsChange(overlayLayerIds.filter(id => id !== layerId))
        } else {
            onOverlayLayerIdsChange([...overlayLayerIds, layerId])
        }
    }

    const spiderSeries = React.useMemo((): TableRowMeanSpiderSeries[] => {
        const series: TableRowMeanSpiderSeries[] = []

        if (primaryVisible) {
            series.push({
                id: "primary",
                name: primaryLayerName ?? "Base",
                rows: primaryRows,
                color: SPIDER_SERIES_COLORS[0],
            })
        }

        overlayLayerIds.forEach((layerId) => {
            const optionIndex = compareOptions.findIndex(entry => entry.layerId === layerId)
            const option = optionIndex >= 0 ? compareOptions[optionIndex] : undefined
            const overlayData = overlayChartDataByLayerId[layerId]
            if (!option || !overlayData) return
            const rows = tableRowMeansForTableNode(overlayData, selectedTableNodeId)
            if (rows.length === 0) return
            series.push({
                id: `overlay-${layerId}`,
                name: option.name,
                rows,
                color: SPIDER_SERIES_COLORS[(optionIndex + 1) % SPIDER_SERIES_COLORS.length],
            })
        })

        return series
    }, [
        primaryVisible,
        primaryLayerName,
        primaryRows,
        overlayLayerIds,
        compareOptions,
        overlayChartDataByLayerId,
        selectedTableNodeId,
    ])

    return (
        <div className={`table-mean-chart${compact ? " table-mean-chart--compact" : ""}`}>
            {tables.length > 1 && (
                <div className="table-mean-chart__select-wrap">
                    <select
                        className="table-mean-chart__select"
                        value={selectedTableNodeId}
                        onChange={(e) => patchSettings({ selectedTableNodeId: Number(e.target.value) })}
                    >
                        {tables.map(table => (
                            <option key={table.nodeId} value={table.nodeId}>{table.name}</option>
                        ))}
                    </select>
                </div>
            )}

            {variant === "bars" && (
                <div className="table-mean-chart__diff-title">
                    <span
                        className="table-mean-chart__diff-order-label"
                        title={chartTitle}
                    >
                        {showingDiff ? (
                            <>
                                <span
                                    className="table-mean-chart__diff-order-name"
                                    style={{ maxWidth: titleNameMaxWidthPx }}
                                >
                                    {minuendName}
                                </span>
                                <span className="table-mean-chart__diff-order-op" aria-hidden="true">→</span>
                                <span
                                    className="table-mean-chart__diff-order-name"
                                    style={{ maxWidth: titleNameMaxWidthPx }}
                                >
                                    {subtrahendName}
                                </span>
                            </>
                        ) : (
                            <span
                                className="table-mean-chart__diff-order-name"
                                style={{ maxWidth: titleNameMaxWidthPx }}
                            >
                                {primaryName}
                            </span>
                        )}
                    </span>
                    {showingDiff && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm table-mean-chart__diff-swap"
                            aria-label="Swap comparison order"
                            title="Swap comparison order"
                            onClick={() => patchSettings({ diffSwapped: !diffSwapped })}
                        >
                            <i className="fas fa-exchange-alt" aria-hidden="true" />
                        </button>
                    )}
                </div>
            )}

            {(variant === "bars") && (
            <div className="table-mean-chart__controls-row">
                <div className="table-mean-chart__control-field">
                    <label className="table-mean-chart__control-label" htmlFor={`${svgId}-sort`}>
                        Sort
                    </label>
                    <select
                        id={`${svgId}-sort`}
                        className="table-mean-chart__select"
                        value={sortType}
                        onChange={(e) => patchSettings({ sortType: e.target.value as TableRowMeanSort })}
                    >
                        <option value="valueDesc">Highest first</option>
                        <option value="valueAsc">Lowest first</option>
                        <option value="tableOrder">Table order</option>
                    </select>
                </div>

                {showCompareSelect && (
                    <div className="table-mean-chart__control-field">
                        <label className="table-mean-chart__control-label" htmlFor={`${svgId}-compare`}>
                            Compare
                        </label>
                        <select
                            id={`${svgId}-compare`}
                            className="table-mean-chart__select"
                            value={compareLayerId ?? ""}
                            onChange={(e) => {
                                const value = e.target.value
                                onCompareLayerIdChange?.(value === "" ? null : Number(value))
                            }}
                        >
                            <option value="">None</option>
                            {compareOptions.map(option => (
                                <option key={option.layerId} value={option.layerId}>{option.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            )}

            {showDiffChart && (
                <div className="table-mean-chart__view-toggle-wrap">
                    <div className="btn-group btn-group-sm table-mean-chart__view-toggle" role="group" aria-label="Chart view">
                        <button
                            type="button"
                            className={`btn ${chartView === "primary" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => patchSettings({ chartView: "primary" })}
                        >
                            Base
                        </button>
                        <button
                            type="button"
                            className={`btn ${chartView === "diff" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => patchSettings({ chartView: "diff" })}
                        >
                            Difference
                        </button>
                    </div>
                </div>
            )}

            {variant === "spider" ? (
                <>
                    <TableRowMeanSpider
                        series={spiderSeries}
                        primaryRows={primaryRows}
                        axisLabel="Area-weighted mean"
                        svgId={svgId}
                        compact={compact}
                        panelWidth={panelWidth}
                        panelHeight={panelHeight}
                        animationResetKey={overlayAnimationKey}
                        enableInitialGrow={true}
                    />
                    {showSpiderLegend && (
                        <div className="table-mean-chart__spider-legend" role="group" aria-label="Layers">
                            <label className="table-mean-chart__spider-legend-item" htmlFor={`${svgId}-legend-primary`}>
                                <input
                                    id={`${svgId}-legend-primary`}
                                    type="checkbox"
                                    checked={primaryVisible}
                                    onChange={() => patchSettings({ primaryVisible: !primaryVisible })}
                                />
                                <span
                                    className="table-mean-chart__spider-legend-swatch"
                                    style={{ backgroundColor: SPIDER_SERIES_COLORS[0].stroke }}
                                />
                                <span title={primaryLayerName ?? "Base"}>{primaryLayerName ?? "Base"}</span>
                            </label>
                            {compareOptions.map((option, index) => {
                                const inputId = `${svgId}-legend-${option.layerId}`
                                const color = SPIDER_SERIES_COLORS[(index + 1) % SPIDER_SERIES_COLORS.length].stroke
                                return (
                                    <label key={option.layerId} className="table-mean-chart__spider-legend-item" htmlFor={inputId}>
                                        <input
                                            id={inputId}
                                            type="checkbox"
                                            checked={overlayLayerIds.includes(option.layerId)}
                                            onChange={() => toggleOverlayLayer(option.layerId)}
                                        />
                                        <span
                                            className="table-mean-chart__spider-legend-swatch"
                                            style={{ backgroundColor: color }}
                                        />
                                        <span title={option.name}>{option.name}</span>
                                    </label>
                                )
                            })}
                        </div>
                    )}
                </>
            ) : (
                <TableRowMeanBars
                    rows={visibleRows}
                    axisLabel="Area-weighted mean"
                    svgId={visibleSvgId}
                    compact={compact}
                    panelWidth={panelWidth}
                    animationResetKey={visibleAnimationKey}
                    enableInitialGrow={!showingDiff}
                    sortType={sortType}
                />
            )}
        </div>
    )
}
